import type { FullBoxScoreData, GameIdSource } from '@parlay-picker/shared/hooks';
import { boxScoreStatsPath, parseGameStatus } from '@parlay-picker/shared/hooks';
import type { LeagueKey } from '@parlay-picker/shared/league';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Fetch box score data via REST endpoint and poll for updates.
 * Fetches game stats via REST.
 * - 'odds_api': hex IDs from legs/picks -> /api/live-stats/:oddsApiGameId
 * - 'bdl': numeric IDs from game picker -> /api/live-stats/game/:bdlGameId
 */
export function useBoxScoreData(
  gameId: string,
  source: GameIdSource,
  leagueKey: LeagueKey,
): FullBoxScoreData | null {
  const [data, setData] = useState<FullBoxScoreData | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(boxScoreStatsPath(gameId, source, leagueKey));
      if (!res.ok) return;
      const json = await res.json();
      if (!json.stats) return;

      const stats = json.stats;
      setData({
        homeTeam: stats.homeTeam,
        awayTeam: stats.awayTeam,
        homeTeamId: stats.homeTeamId,
        awayTeamId: stats.awayTeamId,
        homeScore: stats.homeScore,
        awayScore: stats.awayScore,
        quarter: stats.quarter,
        timeRemaining: stats.timeRemaining,
        gameStatus: parseGameStatus(stats.status),
        players: stats.players,
        quarterScores: stats.quarterScores ?? [],
        lastUpdated: stats.lastUpdated,
      });
    } catch {
      // Ignore fetch errors
    }
  }, [gameId, source, leagueKey]);

  useEffect(() => {
    fetchData();
    // Poll every 30s for updates while component is mounted
    intervalRef.current = setInterval(fetchData, 30_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchData]);

  // Stop polling once game is final — data won't change
  useEffect(() => {
    if (data?.gameStatus === 'final' && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [data?.gameStatus]);

  return data;
}
